/*
 * «Изменить ник» из меню участника: модератор с MANAGE_NICKNAMES меняет ник другому, человек
 * с CHANGE_NICKNAME — себе. Дотянуться до того, чья роль не ниже своей, нельзя — это проверяет
 * сервер, а мы только переводим его отказ на человеческий.
 */
import { useState } from 'react'
import { ApiError } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { guildApi } from '@/lib/guildApi'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { displayName } from '@/store/social'
import { avatarUrl } from '@/lib/avatar'
import { Avatar } from '@/components/Avatar'
import { Modal } from '@/components/settings/Modal'
import { Field } from '@/pages/AuthPage'
import type { User } from '@/types/api'
import { MAX_NICK } from './ServerProfileForm'
import auth from '@/pages/AuthPage.module.css'
import styles from './NickModal.module.css'

interface Props {
  guildId: string
  user: User
  /** Нынешний ник; null — ника нет, видно имя из профиля. */
  nick: string | null
  onClose: () => void
}

export function NickModal({ guildId, user, nick, onClose }: Props) {
  const me = useAuthStore((s) => s.user)
  const applyEvent = useGuildStore((s) => s.applyEvent)
  const [value, setValue] = useState(nick ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMe = me?.id === user.id
  const profileName = user.username ? displayName(user) : ''

  const save = async (next: string | null) => {
    setBusy(true)
    setError(null)
    try {
      const m = isMe ? await guildApi.updateSelf(guildId, next) : await guildApi.updateMember(guildId, user.id, { nick: next })
      applyEvent('GUILD_MEMBER_UPDATE', { ...m, guild_id: guildId })
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.code === 50013) {
        setError(isMe ? 'На этом сервере ник меняют только модераторы.' : 'Нет прав менять ник этому участнику.')
      } else {
        const d = describeError(err)
        setError(d.fields.nick ?? d.message)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Изменить ник" onClose={onClose}>
      <form className={auth.form} onSubmit={(e) => { e.preventDefault(); void save(value.trim() || null) }} noValidate>
        {/* чей ник меняем — строкой с аватаром: имя в заголовке пришлось бы склонять */}
        {!isMe && profileName ? (
          <div className={styles.who}>
            <Avatar initial={profileName.slice(0, 1).toUpperCase()} color="#5865F2" size={32} src={avatarUrl(user.id, user.avatar)} decoration={user.avatar_decoration_data} />
            <span className={styles.names}>
              <span className={styles.name}>{nick ?? profileName}</span>
              <span className={styles.username}>@{user.username}</span>
            </span>
          </div>
        ) : null}
        <Field label="Ник на сервере" htmlFor="member-nick" hint={profileName ? `Пусто — будет видно имя из профиля: ${profileName}.` : 'Пусто — будет видно имя из профиля.'}>
          <input id="member-nick" className={auth.input} maxLength={MAX_NICK} placeholder={profileName} value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        {error ? <p className={auth.error} role="alert">{error}</p> : null}
        <button type="submit" className={auth.primary} disabled={busy || value.trim() === (nick ?? '')}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
        {nick ? (
          <p className={auth.footer}>
            <button type="button" className={auth.link} disabled={busy} onClick={() => void save(null)}>Сбросить ник</button>
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
